<template>
  <div class="sg-page">

    <!-- HERO -->
    <section class="sg-hero">
      <div class="sg-hero-inner">
        <div class="sg-hero-text">
          <p class="sg-eyebrow">Programs</p>
          <h1 class="sg-h1">NexaScale Study Groups.</h1>
          <p class="sg-hero-body">
            A virtual community for people to connect, learn, build projects,
            and get their technical questions answered. We host bi-weekly office 
            hours across multiple tracks, bringing in industry experts to unblock 
            members and push the learning forward.
          </p>
          <a class="sg-btn" href="https://forms.gle/nivXcK3MwJ8ZeFRp7" target="_blank">
            Join the community →
          </a>
        </div>
        <div class="sg-hero-image">
          <img src="../../assets/images/programs/study-groups-min.jpeg" alt="NexaScale Study Groups" />
        </div>
      </div>
    </section>

    <!-- CONTENT -->
    <section class="sg-content">
      <div class="sg-content-inner">

        <div class="sg-section">
          <h2 class="sg-section-title">Our objective</h2>
          <div class="sg-section-body">
            <p>
              The study groups exist to give community members a structured, consistent space 
              to keep building — even when motivation dips and progress feels slow. A lot of 
              people start learning in isolation and stall. Here, you don't have to figure 
              things out alone.
            </p>
            <p>
              We encourage members to work on real projects as they grow, and we create a 
              safe space for peer programming, problem-solving, and getting technical questions 
              actually answered. Every track has its own rhythm — office hours, async discussion, 
              and project collaboration — so there's always something to plug into.
            </p>
            <p>
              Beyond the technical work, the connections people make here matter. Members have 
              found study partners, collaborators, accountability buddies, and friends across 
              six continents — people who show up for each other long after the office hours end.
            </p>
          </div>
        </div>

        <div class="sg-section">
          <h2 class="sg-section-title">How it works</h2>
          <div class="sg-section-body">
            <p>
              Each track runs bi-weekly office hour sessions where we bring in industry experts 
              to answer technical questions, review code, and unblock members on whatever they're 
              stuck on. These aren't lectures — they're working sessions.
            </p>
            <p>
              Between sessions, members are encouraged to post the problems they run into while 
              building, jump into peer programming sessions, and engage in the ongoing track 
              conversations. The learning doesn't stop when the call ends.
            </p>
            <p>
              Every track has a dedicated technical coordinator — a skilled professional who 
              manages the group, ensures members are supported, and maintains the quality of 
              sessions. We also bring in guest speakers and tutors periodically to cover 
              specific topics in depth.
            </p>
          </div>
        </div>

        <div class="sg-section">
          <h2 class="sg-section-title">Available tracks</h2>
          <div class="sg-section-body">
            <p>
              Seven tracks, each with its own community of members, coordinators, and 
              guest experts. Pick the one that fits where you're going — or join more 
              than one if your interests cross disciplines.
            </p>
            <div class="sg-tracks">
              <div class="sg-track">
                <span class="sg-track-dot"></span>
                Artificial Intelligence
                <span class="sg-track-note">ML, LLMs, AI engineering</span>
              </div>
              <div class="sg-track">
                <span class="sg-track-dot"></span>
                Frontend Engineering
              </div>
              <div class="sg-track">
                <span class="sg-track-dot"></span>
                Backend Engineering
                <span class="sg-track-note">across multiple languages</span>
              </div>
              <div class="sg-track">
                <span class="sg-track-dot"></span>
                Cloud Engineering
                <span class="sg-track-note">DevOps, SysAdmin, Infrastructure, SRE</span>
              </div>
              <div class="sg-track">
                <span class="sg-track-dot"></span>
                Mobile App Engineering
              </div>
              <div class="sg-track">
                <span class="sg-track-dot"></span>
                Data Structures &amp; Algorithms
              </div>
              <div class="sg-track">
                <span class="sg-track-dot"></span>
                Data Science
              </div>
              <div class="sg-track">
                <span class="sg-track-dot"></span>
                CyberSecurity
              </div>
            </div>
          </div>
        </div>

        <div class="sg-section sg-section--cta">
          <h2 class="sg-section-title sg-section-title--light">Who can join?</h2>
          <div class="sg-section-body sg-section-body--light">
            <p>
              The study groups are open to everyone — no gatekeeping, no prerequisites, 
              no minimum experience level required.
            </p>
            <p>
              If you're just starting out and trying to find your footing in tech, this 
              community will give you the structure and support to keep going. If you're 
              already experienced and want to sharpen your skills, mentor others, or stay 
              sharp in a fast-moving industry — you're welcome here too.
            </p>
            <p>
              The only thing we ask is that you show up and contribute. The more you put in, 
              the more you get out.
            </p>
            <br />
            <a class="sg-btn sg-btn--yellow" href="https://forms.gle/nivXcK3MwJ8ZeFRp7" target="_blank">
              Join the community →
            </a>
          </div>
        </div>

      </div>
    </section>

  </div>
</template>

<style scoped>
.sg-page {
  min-height: var(--pageheight, 100vh);
  margin-top: 80px;
  text-align: left;
}
.sg-page * { box-sizing: border-box; }
.sg-page p, .sg-page h1, .sg-page h2 { text-align: left; }

/* HERO */
.sg-hero {
  background: #191616;
  padding: 80px 7%;
}
.sg-hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 72px;
  align-items: center;
}
.sg-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #FFB812;
  display: block;
  margin-bottom: 16px;
}
.sg-h1 {
  font-size: clamp(2.2rem, 5vw, 4rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: #ffffff;
  margin: 0 0 20px;
  word-break: break-word;
}
.sg-hero-body {
  font-size: 0.97rem;
  line-height: 1.8;
  color: rgba(255,255,255,0.55);
  margin: 0 0 32px;
  max-width: 520px;
}
.sg-btn {
  display: inline-block;
  background: #FFB812;
  color: #191616;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 12px 26px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s;
}
.sg-btn:hover { background: #FF9914; }
.sg-btn--yellow {
  background: #FFB812;
  color: #191616;
}

.sg-hero-image {
  border-radius: 16px;
  overflow: hidden;
}
.sg-hero-image img {
  width: 100%;
  display: block;
  border-radius: 16px;
  object-fit: cover;
  object-position: center top;
  max-height: 480px;
}

/* CONTENT */
.sg-content {
  background: #ffffff;
}
.sg-content-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 7%;
}

.sg-section {
  padding: 72px 0;
  border-bottom: 1px solid #e8e4e0;
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 60px;
  align-items: start;
}
.sg-section:last-child { border-bottom: none; }
.sg-section--cta {
  background: #191616;
  margin: 0 -7%;
  padding: 72px 7%;
  border-bottom: none;
}

.sg-section-title {
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #191616;
  margin: 0;
  position: sticky;
  top: 100px;
  word-break: break-word;
}
.sg-section-title--light { color: #ffffff; }

.sg-section-body {
  font-size: 0.97rem;
  line-height: 1.8;
  color: #555250;
  margin: 0;
}
.sg-section-body p {
  font-size: 0.97rem;
  line-height: 1.8;
  color: #555250;
  margin: 0 0 16px;
}
.sg-section-body p:last-of-type { margin-bottom: 0; }
.sg-section-body--light,
.sg-section-body--light p { color: rgba(255,255,255,0.6); }

/* TRACKS */
.sg-tracks {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 24px;
  border-top: 1px solid #e8e4e0;
}
.sg-track {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #e8e4e0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #191616;
}
.sg-track-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #FFB812;
  flex-shrink: 0;
}
.sg-track-note {
  font-size: 0.75rem;
  font-weight: 400;
  color: #aaa8a4;
  margin-left: 4px;
}

/* RESPONSIVE */
@media (max-width: 900px) {
  .sg-hero-inner {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .sg-hero-image { order: -1; }
  .sg-hero-image img { max-height: 280px; }
  .sg-section {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 52px 0;
  }
  .sg-section--cta { padding: 52px 5%; margin: 0 -5%; }
  .sg-section-title { position: static; }
  .sg-hero { padding: 60px 5%; }
  .sg-content-inner { padding: 0 5%; }
}
</style>